import { ErrorMessages, ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
import {
  QuestionSheetState,
  setQuestionSheetVisible,
  setQuestionSheetVisibleWithProps
} from 'ducks/questionSheet';
import { BehaviorParameters } from 'rangeConfig/@types';
import extensionSheetBilly from '../images/extensionSheetBilly.png';
import {
  CommunicationSizeComponent,
  Entity,
  GuestConnectionsComponent
} from '@inter-ikea-core/core';
import { ComponentIds } from '@inter-ikea-core/core';
import { getExtensionsOnFrame } from './addDoors';
import {
  addMultipleFrameExtensions,
  rememberAddMultipleFrameExtensions,
  setAddMultipleFrameExtensions
} from '../features/AddMultipleFrameExtensionsFeature';

const addFrameExtensions = ({
  coreApi,
  dispatch,
  props,
  rootState,
  dragAndDropFrame
}: BehaviorParameters & { dragAndDropFrame?: Entity }) => {
  const product = rootState.products.inMarket.find(p => p.coreProductId === props.coreProductId);
  const productType = product?.type;

  if (productType !== ProductType.BILLY_FRAME_EXTENSION || !product?.coreProductId) return;

  const framesInScene = coreApi.entities.getByProductTypes([
    ProductType.BILLY_FRAME_WITH_SHELVES_ASM
  ]);
  const highFramesInScene = framesInScene.filter(
    f =>
      f.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size
        .height === 2020
  );

  const highFramesInSceneWithoutExtensions = highFramesInScene.filter(f => {
    const extensions = getExtensionsOnFrame(f, coreApi);
    return extensions.length === 0;
  });

  const highFramesInSceneWithOneExtension = highFramesInScene.filter(f => {
    const extensions = getExtensionsOnFrame(f, coreApi);

    // Corner should only have one extension
    const isCorner =
      f.getComponent<GuestConnectionsComponent>(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)
        ?.highestPriorityConnection?.guestConnector.key === 'wall-corner';

    return extensions.length === 1 && !isCorner;
  });

  // First add one extension to each frame. Only when user adds and all frames have one ext, we add the 2nd layer
  const addingFirstExtension = highFramesInSceneWithoutExtensions?.length > 0;
  const relevantFrames = addingFirstExtension
    ? highFramesInSceneWithoutExtensions
    : highFramesInSceneWithOneExtension;

  const minRoomHeightForExtension = addingFirstExtension ? 2380 : 2750;
  const roomHeight = coreApi.kvadratRoom?.getDimensions()?.y ?? 0;
  const addingExtensionWillFail = roomHeight < minRoomHeightForExtension;
  const extensionsInMarket = rootState.products.inMarket.filter(
    p => p.type === ProductType.BILLY_FRAME_EXTENSION
  );

  const allExtensionsMaxedOut =
    highFramesInScene.length > 0 &&
    highFramesInSceneWithoutExtensions.length === 0 &&
    highFramesInSceneWithOneExtension.length === 0;

  const determinePlacementAttemptErrorMessage = () => {
    if (addingExtensionWillFail) {
      return ErrorMessages.BillyExtensionWontFit;
    } else if (allExtensionsMaxedOut) {
      return ErrorMessages.BillyExtensionMaxTwo;
    } else if (framesInScene.length - highFramesInScene.length > 0 && !dragAndDropFrame) {
      return ErrorMessages.BillyExtensionSmallFrame;
    }
    return null;
  };
  const placementAttemptErrorMessage = determinePlacementAttemptErrorMessage();
  if (placementAttemptErrorMessage) {
    coreApi.emit.onPlacementAttempt(placementAttemptErrorMessage);
  }

  if (allExtensionsMaxedOut) {
    coreApi.products.addToTarget(props.coreProductId, highFramesInScene[0]);
    return;
  }

  const addSingleExtension = () => {
    if (dragAndDropFrame) {
      coreApi.products.addToTarget(product.coreProductId, dragAndDropFrame);
    } else {
      coreApi.products.add(product.coreProductId);
    }
  };

  const addMultipleExtensions = (frames: Entity[]) => {
    frames.forEach(frame => {
      const extensions = getExtensionsOnFrame(frame, coreApi);

      if (extensions.length > 1) return;

      // if extension exist, we should add the 2nd extension to that entity
      const entity = extensions?.[0] ?? frame;

      // match entity color and size with extension and add it
      const entitySize = entity.getComponent<CommunicationSizeComponent>(
        ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
      )?.size;

      const frameDesign = entity?.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
        ?.design;

      const frameExtension = extensionsInMarket.find(extension => {
        return (
          entitySize?.width === extension.communicatedDimensions?.width &&
          entitySize?.depth === extension.communicatedDimensions?.depth &&
          frameDesign === extension.design
        );
      });

      if (frameExtension) {
        coreApi.products.addToTarget(frameExtension.coreProductId, entity);
      }
    });
  };

  if (
    highFramesInScene.length < 1 ||
    (highFramesInSceneWithoutExtensions.length === 0 &&
      highFramesInSceneWithOneExtension.length === 0)
  ) {
    return;
  } else if (
    highFramesInSceneWithoutExtensions.length === 1 ||
    (highFramesInSceneWithoutExtensions.length === 0 &&
      highFramesInSceneWithOneExtension.length === 1)
  ) {
    addSingleExtension();
    return;
  }

  const addMultiple = addMultipleFrameExtensions(rootState);
  const rememberAddMultiple = rememberAddMultipleFrameExtensions(rootState);
  const shouldTriggerSheet = !rememberAddMultiple && !addingExtensionWillFail;

  if (shouldTriggerSheet) {
    const frame202 = rootState.products.inMarket.find(
      p =>
        p.type === ProductType.BILLY_FRAME_WITH_SHELVES_ASM &&
        p.communicatedDimensions.height === 2020
    );
    if (!frame202) return;
    const height = frame202.communicatedDimensionsLocalized.height;
    const unit = frame202.communicatedDimensionsLocalized.unit;

    dispatch(
      setQuestionSheetVisibleWithProps({
        modalVisible: true,
        title: 'storageone.frameExtensionSheetTitle',
        preamble: 'storageone.frameExtensionSheetBody',
        afterImageDescription: {
          text: 'storageone.frameExtensionSheetBody2',
          props: { height, unit }
        },
        primaryButtonText: 'storageone.frameExtensionSheetPrimaryAction',
        secondaryButtonText: 'storageone.frameExtensionSheetSecondaryAction',
        onAccept: () => {
          addMultipleExtensions(relevantFrames);
          dispatch(setAddMultipleFrameExtensions(true));
          dispatch(setQuestionSheetVisible(false));
        },
        onDismiss: () => {
          addSingleExtension();
          dispatch(setAddMultipleFrameExtensions(false));
          dispatch(setQuestionSheetVisible(false));
        },
        imageRealtivePath: extensionSheetBilly,
        imageHorizontalPadding: 40
      } as QuestionSheetState)
    );
  } else {
    if (addMultiple || addingExtensionWillFail) {
      addMultipleExtensions(relevantFrames);
    } else {
      addSingleExtension();
    }
  }
};

export default addFrameExtensions;
